import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { SectionBase } from '../../../components/section/section-base';
import { BusinessParamName } from '../../../models/request-offer/request-offer.enum';
import { InsuranceCompanyCode } from '../../../models/insurance-company-code.enum';
import { ApplicationStateService } from '../../../services/application-state.service';
import { LanguageService } from '../../../services/language.service';
import { OfferModificationDestination } from '../../../../core/consts/offer-modification-destination.enum';
import { OfferModificationCode } from '../../../../core/consts/offer-modification-code.enum';
import { OfferModificationsService } from '../../../../core/services/impl/offer-modifications/offer-modifications.service';
import { OfferModification } from '../../../../core/models/offer/offer-modification.model';
import { Offer } from '../../../../core/models/offer/offer.model';
import { RegistrationService } from '../../../../core/services/impl/registration.service';
import { MatDialog } from '@angular/material';
import { isNotNullOrUndefined } from 'codelyzer/util/isNotNullOrUndefined';
import { AcVariantsBeesafeSectionConfigSectionConfig } from '../ac-variants-beesafe-section/ac-variants-beesafe-section.config';
import { BeesafeCoverageComparisonDialogComponent } from './beesafe-coverage-comparision-dialog/beesafe-coverage-comparison-dialog.component';
import {isKIOSKPOLIS, noop} from '../../../../core/utils/utils';
import { Subscription } from 'rxjs/Rx';
import {BannersService} from '../../../services/banners.service';
import {
  OfferModificationConfig,
  OfferModificationsConfigService
} from '../../../../core/services/impl/offer-modifications/offer-modifications-config.service';

@Component({
  selector: 'app-additional-options-beesafe-section',
  templateUrl: './additional-options-beesafe-section.component.html',
  styleUrls: ['./additional-options-beesafe-section.component.scss']
})
export class AdditionalOptionsBeesafeSectionComponent extends SectionBase implements OnInit, OnDestroy {

  modificationsConfigs: OfferModificationConfig[];
  modifications: OfferModification[];
  allModificationsVisible: boolean;
  sectionVisible: boolean;
  premiumEventSubscription: Subscription;

  expandModificationsButtonVisible: boolean;

  private readonly MODIFICATIONS_ORDER = [
    OfferModificationCode.ASSISTANCE,
    OfferModificationCode.TIRES,
    OfferModificationCode.BATTERY,
    OfferModificationCode.NNW,
    OfferModificationCode.GLASS,
    OfferModificationCode.DIRECT_LOSS_SETTLEMENT,
    OfferModificationCode.LEGAL_PROTECTION,
    OfferModificationCode.LUGGAGE,
    OfferModificationCode.SPORTS_EQUIPMENT
  ]

  get visibleModifications(): OfferModification[] {
    return this.modifications.filter(mod => mod.visible);
  }

  get invisibleModifications(): OfferModification[] {
    return this.modifications.filter(mod => !mod.visible);
  }

  get selectedOffer(): Offer {
    return this.offerModificationsService.selectedOfferService.selectedOffer;
  }

  get insuranceCompanyCode() {
    return this.stateService.getBusinessParameterValue(BusinessParamName.INSURANCE_COMPANY_CODE);
  }

  get bannerVisible(): boolean {
    return this.sectionVisible && this.bannersService.paymentBannerVisible && isKIOSKPOLIS();
  }

  constructor(private languageService: LanguageService,
              private bannersService: BannersService,
              private registration: RegistrationService,
              private cdr: ChangeDetectorRef,
              private dialog: MatDialog,
              private stateService: ApplicationStateService,
              private offerModificationsService: OfferModificationsService,
              private configService: OfferModificationsConfigService) {
    super();
  }

  ngOnInit() {
    this.initModifications();
    this.initSectionVisible();
    if (this.sectionVisible) {
      this.sortModifications();
      this.initBean();
      this.createConfig();
      this.buildForm();
      this.initControlsChanges();

      this.offerModificationsService.setOfferBasicPrice();
      this.offerModificationsService.setOfferCurrentPrice();
      this.offerModificationsService.initCache();
      this.offerModificationsService.setRegistrationModificationCoverages();
      this.offerModificationsService.setRegistrationCurrentAmount();

      this.getControl('ASSISTANCE').updateValueAndValidity({onlySelf: true});

      this.premiumEventSubscription = this.registration.premiumChangeEvent.subscribe(() => {
        this.offerModificationsService.setOfferCurrentPrice();
        this.offerModificationsService.setRegistrationCurrentAmount();
        this.rebuildSection()
      });
    }
  }

  ngOnDestroy(): void {
    this.premiumEventSubscription ? this.premiumEventSubscription.unsubscribe() : noop();
  }

  createConfig(): void {
    const priceFrequency = this.offerModificationsService.getCorrectPriceFrequency();
    const config = new AcVariantsBeesafeSectionConfigSectionConfig(this.languageService, this.offerModificationsService, priceFrequency);
    this.formConfig = config.createConfig(OfferModificationDestination.ADDITIONAL_OPTIONS, this.modifications, true);
  }

  initBean(): void {
    this.bean = {};
    this.modifications.forEach(modification => {
      const selectedOption = modification.options.find(option => option.selected);
      if (selectedOption) {
        this.bean[modification.code] = selectedOption.value;
      }
    })
  }

  initControlsChanges(): void {
    this.modifications.forEach(modification => this.getControl(modification.code).valueChanges.subscribe(value => {
      this.offerModificationsService.updateModification(modification.code, value);
      this.offerModificationsService.saveModificationInCache();

      this.offerModificationsService.setOfferCurrentPrice();
      this.offerModificationsService.modifiedOfferEvent.emit();
    }));

    if (isNotNullOrUndefined(this.getControl('ASSISTANCE'))) {
      this.getControl('ASSISTANCE').valueChanges.subscribe(value => {
        this.offerModificationsService.tiresValue = 'TIRES_YES';
        this.offerModificationsService.batteryValue = 'BATTERY_YES';

        this.offerModificationsService.updateModification(OfferModificationCode.TIRES, false);
        this.offerModificationsService.updateModification(OfferModificationCode.BATTERY, false);

        this.rebuildSection();

        const batteryControl = this.getControl(OfferModificationCode.BATTERY);
        const tiresControl = this.getControl(OfferModificationCode.TIRES);

        if (value === 'ASSISTANCE_STANDARD' || value === 'ASSISTANCE_PREMIUM') {
          tiresControl ? tiresControl.setValue(this.offerModificationsService.tiresValue, {emitEvent: false}) : noop();
          batteryControl ? batteryControl.setValue(this.offerModificationsService.batteryValue, {emitEvent: false}) : noop();
        } else {
          tiresControl ? tiresControl.setValue(false, {emitEvent: false}) : noop();
          batteryControl ? batteryControl.setValue(false, {emitEvent: false}) : noop();
        }

        this.offerModificationsService.setOfferCurrentPrice();
        this.offerModificationsService.modifiedOfferEvent.emit();
      });
    }
  }

  toggleVisibility(): void {
    this.allModificationsVisible = !this.allModificationsVisible;
  }

  compareCoverages(code: string): void {
    const pricing = code === 'ASSISTANCE' ? this.offerModificationsService.getAssistancePricing() : this.offerModificationsService.getNNWPricing();

    this.dialog.open(BeesafeCoverageComparisonDialogComponent, {
      disableClose: true,
      panelClass: ['app-offers-dialog', 'offer-modifications-comparison-dialog-panel', `offer-modifications-comparison-dialog-panel--${code}`],
      data: {
        mode: code,
        pricing: pricing,
        config: this.configService.getModificationConfig(<OfferModificationCode>code)
      }
    }).afterClosed().subscribe(variant => {
      if (isNotNullOrUndefined(variant)) {
        code === 'ASSISTANCE' ? this.getControl(OfferModificationCode.ASSISTANCE).setValue(variant) : this.getControl(OfferModificationCode.NNW).setValue(variant);
      }
    });
  }

  private initModifications(): void {
    this.modificationsConfigs = this.configService.getModificationsForDestination(OfferModificationDestination.ADDITIONAL_OPTIONS);
    const codes = this.modificationsConfigs.map(config => config.code);
    if (this.offerModificationsService.modifications && this.offerModificationsService.modifications.length > 0) {
      this.modifications = this.offerModificationsService.modifications.filter(modification => codes.includes(<OfferModificationCode>modification.code));
      this.expandModificationsButtonVisible = this.modifications.map(mod => mod.visible)
          .filter(visible => !visible)
          .length > 1;
      this.offerModificationsService.tiresValue = this.offerModificationsService.selectedTiresOption ? this.offerModificationsService.selectedTiresOption.value : null;
      this.offerModificationsService.batteryValue = this.offerModificationsService.selectedBatteryOption ? this.offerModificationsService.selectedBatteryOption.value : null;
    }
  }

  private initSectionVisible(): void {
    const isBeesafe = this.stateService.getBusinessParameterValue(BusinessParamName.INSURANCE_COMPANY_CODE) === InsuranceCompanyCode.BEESAFE;
    this.sectionVisible = isBeesafe && this.modifications && this.modifications.length > 0;
  }

  private sortModifications(): void {
    const sortedModifications = [];
    this.MODIFICATIONS_ORDER.forEach(modificationCode => {
      const modification = this.modifications.find(mod => mod.code === modificationCode);
      if (isNotNullOrUndefined(modification)) {
        sortedModifications.push(modification)
      }
    });
    this.modifications = sortedModifications;
  }

  private rebuildSection(): void {
    this.initBean();
    this.createConfig();
    this.buildForm();
  }

}
